import {useTranslation} from "react-i18next";
import React, {FC, lazy, PropsWithChildren, Suspense, useState} from "react";
import {WithMenuLayout} from "@layouts/with-menu-layout";
import {HeaderTabs} from "@features/header-tabs";
import {Outlet} from "react-router";
import {ROUTES} from "@routes/routes-list";
import {useOrdersList} from "@entities/orders/lib/hooks";
import {
    new_orders_tab_selector,
    now_orders_tab_selector,
    pre_orders_tab_selector,
    receiving_type_filter_selector
} from "@entities/orders/lib/selectors";
import {
    RECEIVING_TYPE_FILTER,
    useReceivingTypeFilter
} from "@entities/orders/lib/receiving-type-filter";
import Dropdown from "@shared/ui/dropdown";
import {DotsButton} from "@shared/ui/dots-button";
import {ModalBase} from "@features/modal/base-modal";
import {useWindow} from "@shared/lib/hooks";
import {useCompany} from "@entities/company/lib/hooks";
import {COMPANY_ADDRESS_STATUS} from "@dto/CompanyAddressDTO";
import dayjs from "dayjs";
import {DateFormat} from "@features/date/lib/date-format";
import {useDateFormatter} from "@features/date/lib/useDateFormatter";
import {enumToString} from "@shared/lib/helpers";
import {Pill} from "@shared/ui/pill";
import classNames from "classnames";
import {CheckCircle, FunnelIcon, PauseIcon} from "@features/icons";

const StatusSelector = lazy(() => import("@entities/sidebar/ui/status-selector"));
export const OrdersPageLayout: FC<PropsWithChildren> = ({children}) => {
    const {t} = useTranslation();
    const {groupedOrders} = useOrdersList()
    const headerLinks = [
        {
            title: t("new_orders"),
            route: ROUTES.NEW_ORDERS,
            badge: String(groupedOrders.newOrders?.length || ''),
            'data-testid': new_orders_tab_selector,
        },
        {
            title: t("now_orders"),
            route: ROUTES.NOW_ORDERS,
            badge: String(groupedOrders.now?.length || ''),
            'data-testid': now_orders_tab_selector,
        },
        {
            title: t("later_orders"),
            route: ROUTES.LATER_ORDERS,
            badge: String(groupedOrders.preorder?.length || ''),
            'data-testid': pre_orders_tab_selector,
        },
    ];
    const {getDateFormat} = useDateFormatter()
    const [isStatusOpen, setStatusOpen] = useState(false)
    const {receivingTypeFilter, setReceivingTypeFilter} = useReceivingTypeFilter()
    const [isFilterOpen, setFilterOpen] = useState(false)
    const [draftReceivingType, setDraftReceivingType] = useState<RECEIVING_TYPE_FILTER>(receivingTypeFilter)
    const {isTablet} = useWindow()

    const receivingTypeOptions = [
        {value: RECEIVING_TYPE_FILTER.DELIVERY, title: t('delivery')},
        {value: RECEIVING_TYPE_FILTER.PICKUP, title: t('pickup')},
    ]

    const isFilterActive = receivingTypeFilter !== RECEIVING_TYPE_FILTER.ALL

    const openFilterModal = () => {
        setDraftReceivingType(receivingTypeFilter)
        setFilterOpen(true)
    }

    const renderFilterButton = () => {
        return (
            <DotsButton
                data-testid={receiving_type_filter_selector}
                onClick={openFilterModal}
                icon={<FunnelIcon/>}
                className={classNames(
                    {"p-[0!important]": isTablet},
                    "btn header-btn relative btn-info flex gap-2.5 items-center justify-center",
                )}
            >
                {isFilterActive && <span
                  className="flex badge badge-circle badge-info absolute top-2 right-2 md:right-0 md:left-7 md:top-5"></span>}
                <span className={"hidden md:inline"}>{t("filter")}</span>
            </DotsButton>
        )
    }

    const renderFilterModal = () => {
        return (
            <ModalBase maxWidth={'max-w-sm mx-6'} onConfirm={async () => {
                setReceivingTypeFilter(draftReceivingType)
            }} header={t('filters')} isOpen={isFilterOpen} onClose={async () => setFilterOpen(false)}>
                {/* reserves room for the opened dropdown so its options are not cut off by the modal scroll */}
                <div className={'flex flex-col gap-2 min-h-[260px]'}>
                    <label>{t('delivery_type')}</label>
                    <Dropdown
                        canSelectAll
                        value={draftReceivingType === RECEIVING_TYPE_FILTER.ALL ? undefined : draftReceivingType}
                        onSelect={(option) => setDraftReceivingType((option.value as RECEIVING_TYPE_FILTER) ?? RECEIVING_TYPE_FILTER.ALL)}
                        options={receivingTypeOptions}/>
                </div>
            </ModalBase>
        )
    }

    const {data: company} = useCompany(true)

    const iconsMapping = {
        [COMPANY_ADDRESS_STATUS.ACTIVE]: (
            <CheckCircle className={"text-green-500 w-6 h-6"}/>
        ),
        [COMPANY_ADDRESS_STATUS.HIDDEN]: (
            <CheckCircle className={"text-green-500 w-6 h-6"}/>
        ),
        [COMPANY_ADDRESS_STATUS.INACTIVE]: (
            <PauseIcon className={"text-orange-400 w-6 h-6"}/>
        ),
    };


    const renderTime = () => {
        if (!company) return null
        if (company.companyAddress.status === COMPANY_ADDRESS_STATUS.INACTIVE) {
            if (!company.companyAddress.scheduledStatusUpdateTime) {
                return t(enumToString(company.companyAddress.status, COMPANY_ADDRESS_STATUS, 'company_address'))
            }
            const dueDate = dayjs.unix(company.companyAddress.scheduledStatusUpdateTime)
            let time = dueDate.format(getDateFormat(DateFormat.DATE_SHORT) + ' ' + getDateFormat(DateFormat.TIME))
            if (dueDate.isToday()) {
                time = dueDate.format(getDateFormat(DateFormat.TIME))
            }
            return `${t('paused_till')} ${time}`
        }
        return null
    }

    const renderBadge = (isDesktop = true) => {
        const text = renderTime()
        if (!text) return null
        if(!company) return null
        return <Pill className={classNames('cursor-pointer ml-auto lg:ml-0', {
            'hidden lg:flex': !isDesktop,
            'flex: lg:hidden': isDesktop
        })} onClick={() => setStatusOpen(true)} type={'warning'}>
            {iconsMapping[company.companyAddress.status]} {text}
        </Pill>
    }

    return (
        <WithMenuLayout headerContent={<div className={'flex items-center gap-4 flex-grow'}>
            {/* mirrored flex-1 side blocks keep the tabs centered despite the filter button on the right */}
            <div className={'hidden md:block flex-1'}/>
            <HeaderTabs items={headerLinks}/>
            <div className={'md:flex-1 flex items-center justify-end gap-4'}>
                {renderBadge(false)}
                {renderFilterButton()}
            </div>
        </div>}>
            {
                company && <Suspense fallback={''}>
                <StatusSelector
                  isOpen={isStatusOpen}
                  onClose={() => {
                      setStatusOpen(false);
                      return Promise.resolve();
                  }}
                />
              </Suspense>
            }
                {renderBadge(true)}
            {renderFilterModal()}
            <Outlet/>
        </WithMenuLayout>
    );
};
